/**
 * QC-ANALYTICS-EXT-001 (Aug 2026) — extra tabs for Advanced QC Analytics:
 *   • QCSpecialistTab   — per-reviewer activity, Tax QC vs Title Search QC
 *   • TypingActivityTab — per-typist activity (Title Search / Typing workflow)
 *   • ErrorAnalyticsTab — returns grouped by Error Category + Severity with
 *                         expandable drill-down rows
 * Presentation-only: data + CSV export handlers live in QCAdvancedAnalytics.
 */

import React, { useState } from 'react';
import { Download, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { formatDateTimeLocal } from '../../utils/formatters';

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

const MetaNote = ({ metadata }) => {
  if (!metadata) return null;
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800 flex items-start gap-2">
      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>
        <p><span className="font-semibold">Date Filter:</span> {metadata.date_filter_basis}</p>
        {metadata.note && <p className="mt-0.5">{metadata.note}</p>}
        {metadata.source && <p className="mt-0.5 text-blue-600">{metadata.source}</p>}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, tone = 'slate', testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
    <p className="text-sm text-slate-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 text-${tone}-700`}>{value}</p>
  </div>
);

/* ── QC Specialist Activity ──────────────────────────────────── */
export const QCSpecialistTab = ({ data, loading, onExport }) => {
  if (loading) return <Spinner />;
  const specialists = data?.specialists || [];
  const totals = data?.totals || {};
  return (
    <div data-testid="qc-specialist-tab">
      <MetaNote metadata={data?.metadata} />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <KpiCard label="QC Specialists" value={totals.total_specialists ?? 0} testId="qcspec-kpi-specialists" />
        <KpiCard label="Reviews" value={totals.total_reviews ?? 0} testId="qcspec-kpi-reviews" />
        <KpiCard label="Approvals" value={totals.total_approvals ?? 0} tone="green" testId="qcspec-kpi-approvals" />
        <KpiCard label="Returns" value={totals.total_returns ?? 0} tone="red" testId="qcspec-kpi-returns" />
        <KpiCard label="Tax / Title" value={`${totals.tax_reviews ?? 0} / ${totals.title_reviews ?? 0}`} testId="qcspec-kpi-split" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">QC Specialist Activity</h3>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="export-qcspecialist-csv">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-700 text-white text-left">
                <th className="p-3 font-medium">QC Specialist</th>
                <th className="p-3 font-medium text-center">Reviews</th>
                <th className="p-3 font-medium text-center">Approvals</th>
                <th className="p-3 font-medium text-center">Returns</th>
                <th className="p-3 font-medium text-center">Critical</th>
                <th className="p-3 font-medium text-center">Major</th>
                <th className="p-3 font-medium text-center">Minor</th>
                <th className="p-3 font-medium text-center bg-blue-800">Tax QC (A/R)</th>
                <th className="p-3 font-medium text-center bg-blue-800">Title QC (A/R)</th>
              </tr>
            </thead>
            <tbody>
              {specialists.map(s => (
                <tr key={s.reviewer_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`qcspec-row-${s.reviewer_id}`}>
                  <td className="p-3 font-medium text-slate-800">{s.reviewer_name}</td>
                  <td className="p-3 text-center">{s.total_reviews}</td>
                  <td className="p-3 text-center text-green-600 font-medium">{s.approvals}</td>
                  <td className="p-3 text-center text-red-600 font-medium">{s.returns}</td>
                  <td className="p-3 text-center">{s.critical_returns}</td>
                  <td className="p-3 text-center">{s.major_returns}</td>
                  <td className="p-3 text-center">{s.minor_returns}</td>
                  <td className="p-3 text-center bg-slate-50">
                    <span className="font-medium">{s.tax?.reviews ?? 0}</span>
                    <span className="text-xs text-slate-400 ml-1">({s.tax?.approvals ?? 0}/{s.tax?.returns ?? 0})</span>
                  </td>
                  <td className="p-3 text-center bg-slate-50">
                    <span className="font-medium">{s.title?.reviews ?? 0}</span>
                    <span className="text-xs text-slate-400 ml-1">({s.title?.approvals ?? 0}/{s.title?.returns ?? 0})</span>
                  </td>
                </tr>
              ))}
              {specialists.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-slate-500">No QC Specialist activity in this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ── Typing Activity ─────────────────────────────────────────── */
export const TypingActivityTab = ({ data, loading, onExport }) => {
  if (loading) return <Spinner />;
  const typists = data?.typists || [];
  const totals = data?.totals || {};
  return (
    <div data-testid="typing-activity-tab">
      <MetaNote metadata={data?.metadata} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Typing Processors" value={totals.total_typists ?? 0} testId="typing-kpi-typists" />
        <KpiCard label="Claimed / Assigned" value={totals.total_assigned ?? 0} testId="typing-kpi-assigned" />
        <KpiCard label="Completed" value={totals.total_completed ?? 0} tone="green" testId="typing-kpi-completed" />
        <KpiCard label="Returned to Search" value={totals.total_returned_to_search ?? 0} tone="amber" testId="typing-kpi-returned" />
        <KpiCard label="Returned to QC" value={totals.total_returned_to_qc ?? 0} tone="amber" testId="typing-kpi-returned-qc" />
        <KpiCard label="Checklist Corrections" value={totals.total_corrections ?? 0} testId="typing-kpi-corrections" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Typing Processor Activity</h3>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="export-typing-csv">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-700 text-white text-left">
                <th className="p-3 font-medium">Typing Processor</th>
                <th className="p-3 font-medium text-center">Claimed / Assigned</th>
                <th className="p-3 font-medium text-center">Completed</th>
                <th className="p-3 font-medium text-center">Returned to Search</th>
                <th className="p-3 font-medium text-center">Returned to QC</th>
                <th className="p-3 font-medium text-center">Checklist Corrections</th>
              </tr>
            </thead>
            <tbody>
              {typists.map(t => (
                <tr key={t.typist_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`typing-row-${t.typist_id}`}>
                  <td className="p-3 font-medium text-slate-800">{t.typist_name}</td>
                  <td className="p-3 text-center">{t.assigned}</td>
                  <td className="p-3 text-center text-green-600 font-medium">{t.completed}</td>
                  <td className="p-3 text-center text-amber-600 font-medium">{t.returned_to_search}</td>
                  <td className="p-3 text-center text-amber-600 font-medium" data-testid={`typing-row-${t.typist_id}-returned-qc`}>{t.returned_to_qc ?? 0}</td>
                  <td className="p-3 text-center">{t.corrections}</td>
                </tr>
              ))}
              {typists.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-slate-500">No Typing activity in this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ── Error Analytics ─────────────────────────────────────────── */
const NameChips = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">{title}</p>
    <div className="flex flex-wrap gap-1">
      {(items || []).map(i => (
        <span key={i.name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-200 rounded-full text-xs text-slate-700">
          {i.name}<span className="text-slate-400">×{i.count}</span>
        </span>
      ))}
      {(!items || items.length === 0) && <span className="text-xs text-slate-400">—</span>}
    </div>
  </div>
);

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-700',
  major: 'bg-amber-100 text-amber-700',
  minor: 'bg-blue-100 text-blue-700',
  unspecified: 'bg-slate-100 text-slate-500',
};

export const ErrorAnalyticsTab = ({ data, loading, onExport }) => {
  const [expanded, setExpanded] = useState(null);
  if (loading) return <Spinner />;
  const categories = data?.categories || [];
  const totals = data?.totals || {};
  return (
    <div data-testid="error-analytics-tab">
      <MetaNote metadata={data?.metadata} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Total Returns / Errors" value={totals.total_errors ?? 0} tone="red" testId="errors-kpi-total" />
        <KpiCard label="Error Categories" value={totals.total_categories ?? 0} testId="errors-kpi-categories" />
        <KpiCard label="Critical" value={totals.critical ?? 0} tone="red" testId="errors-kpi-critical" />
        <KpiCard label="Major / Minor" value={`${totals.major ?? 0} / ${totals.minor ?? 0}`} testId="errors-kpi-majmin" />
        <KpiCard label="Tax / Title" value={`${totals.tax_errors ?? 0} / ${totals.title_errors ?? 0}`} testId="errors-kpi-split" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">Error Categories</h3>
            <p className="text-xs text-slate-500 mt-0.5">Click a row to see affected processors, QC specialists, clients, products and orders</p>
          </div>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="export-errors-csv">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-700 text-white text-left">
                <th className="p-3 font-medium w-8" />
                <th className="p-3 font-medium">Error Category</th>
                <th className="p-3 font-medium text-center">Errors</th>
                <th className="p-3 font-medium text-center">% of Total</th>
                <th className="p-3 font-medium text-center">Minor</th>
                <th className="p-3 font-medium text-center">Major</th>
                <th className="p-3 font-medium text-center">Critical</th>
                <th className="p-3 font-medium text-center">Tax</th>
                <th className="p-3 font-medium text-center">Title Search</th>
                <th className="p-3 font-medium text-center">Processors</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <React.Fragment key={c.category}>
                  <tr
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpanded(expanded === c.category ? null : c.category)}
                    data-testid={`error-cat-row-${c.category}`}
                  >
                    <td className="p-3 text-slate-400">
                      {expanded === c.category ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="p-3 font-medium text-slate-800">{c.category_label}</td>
                    <td className="p-3 text-center font-semibold text-red-600">{c.count}</td>
                    <td className="p-3 text-center">{c.pct_of_total}%</td>
                    <td className="p-3 text-center">{c.minor}</td>
                    <td className="p-3 text-center">{c.major}</td>
                    <td className="p-3 text-center font-medium text-red-600">{c.critical}</td>
                    <td className="p-3 text-center">{c.tax_count}</td>
                    <td className="p-3 text-center">{c.title_count}</td>
                    <td className="p-3 text-center">{(c.processors || []).length}</td>
                  </tr>
                  {expanded === c.category && (
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <td colSpan={10} className="p-4" data-testid={`error-cat-detail-${c.category}`}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <NameChips title="Affected Processors" items={c.processors} testId={`error-detail-processors-${c.category}`} />
                          <NameChips title="QC Specialists" items={c.qc_specialists} testId={`error-detail-qc-${c.category}`} />
                          <NameChips title="Clients" items={c.clients} testId={`error-detail-clients-${c.category}`} />
                          <NameChips title="Products" items={c.products} testId={`error-detail-products-${c.category}`} />
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-slate-100 text-slate-600 text-left">
                                <th className="p-2 font-medium">Order #</th>
                                <th className="p-2 font-medium">Severity</th>
                                <th className="p-2 font-medium">Workflow</th>
                                <th className="p-2 font-medium">Processor</th>
                                <th className="p-2 font-medium">QC Specialist</th>
                                <th className="p-2 font-medium">Client</th>
                                <th className="p-2 font-medium">Product</th>
                                <th className="p-2 font-medium">Returned At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(c.orders || []).map((o, idx) => (
                                <tr key={`${o.order_id}-${idx}`} className="border-t border-slate-100">
                                  <td className="p-2 font-medium text-blue-700">{o.order_number}</td>
                                  <td className="p-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${SEVERITY_BADGE[o.severity] || SEVERITY_BADGE.unspecified}`}>
                                      {o.severity}
                                    </span>
                                  </td>
                                  <td className="p-2">{o.workflow}</td>
                                  <td className="p-2">{o.processor_name}</td>
                                  <td className="p-2">{o.qc_specialist_name}</td>
                                  <td className="p-2">{o.client_code}</td>
                                  <td className="p-2">{o.product}</td>
                                  <td className="p-2 text-slate-500">{o.at ? formatDateTimeLocal(o.at) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {categories.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-slate-500">No returns for correction in this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
